import { toEpochMs } from "~/v3-poc/utils/time";
import type { OptionContractWithSnapshot } from "../../hooks/useGetOptionsData";
import type {
  ChainLeg,
  ChainQuote,
  ChainRow,
  ExpirationCadence,
  ExpirationFilter,
  ExpirationOption,
  ExpirationRange,
  ValueFreshness,
} from "./optionsChain.types";

/** Beyond this a "15 minutes delayed" label is a lie; the contract just isn't trading. */
const STALE_TRADE_SECONDS = 1_800;

/** A frozen chain is only detectable from timestamp age, so allow some slack. */
const CLOSED_MARKET_SLACK_SECONDS = 120;

export const MISSING_VALUE = "—";

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const formatPrice = (value: number | undefined, dp = 2) =>
  value === undefined ? MISSING_VALUE : value.toFixed(dp);

export const formatInteger = (value: number | undefined) =>
  value === undefined ? MISSING_VALUE : value.toLocaleString("en-US");

export const formatGreek = (value: number | undefined, dp = 3) =>
  value === undefined ? MISSING_VALUE : value.toFixed(dp);

export const formatImpliedVolatility = (value: number | undefined) =>
  value === undefined ? MISSING_VALUE : `${(value * 100).toFixed(1)}%`;

/** Signed so a reader can tell a flat contract from a missing value. */
export const formatSignedPrice = (value: number | undefined) => {
  if (value === undefined) {
    return MISSING_VALUE;
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}`;
};

/**
 * A penny contract that moved a penny is up 100%, which is true and useless. The
 * value is still shown, capped in width, because hiding it would be worse than
 * printing a large number.
 */
export const formatSignedPercent = (value: number | undefined) => {
  if (value === undefined) {
    return MISSING_VALUE;
  }
  const sign = value > 0 ? "+" : "";
  if (Math.abs(value) >= 1000) {
    return `${sign}${Math.round(value)}%`;
  }
  return `${sign}${value.toFixed(1)}%`;
};

export const formatStrike = (strike: number) =>
  strike.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ---------------------------------------------------------------------------
// Freshness
// ---------------------------------------------------------------------------

export interface FreshnessInput {
  timestamp?: number;
  /** True when the value only ever arrives on the delayed path. */
  isDelayedFeed: boolean;
  isMarketOpen?: boolean;
  now?: number;
}

/**
 * Classifies one value's freshness. Deliberately per value rather than per row:
 * on the default feed the spread is current while the last trade is 15 minutes
 * behind, so a single row-level label would be wrong in both directions.
 */
export const classifyFreshness = ({
  timestamp,
  isDelayedFeed,
  isMarketOpen,
  now = Date.now(),
}: FreshnessInput): ValueFreshness => {
  const ageSeconds =
    timestamp === undefined ? undefined : Math.max(0, (now - timestamp) / 1000);

  if (isMarketOpen === false) {
    if (ageSeconds === undefined || ageSeconds > CLOSED_MARKET_SLACK_SECONDS) {
      return "closed";
    }
  }

  if (ageSeconds !== undefined && ageSeconds > STALE_TRADE_SECONDS) {
    return "stale";
  }

  if (isDelayedFeed) {
    return "delayed";
  }

  return "live";
};

// ---------------------------------------------------------------------------
// Expirations
// ---------------------------------------------------------------------------

/** UTC parse so a local timezone cannot shift an expiry across a day boundary. */
const parseIsoDate = (date: string) => new Date(`${date}T00:00:00Z`);

export const daysToExpiration = (date: string, now = Date.now()): number => {
  const expiry = parseIsoDate(date).getTime();
  if (!Number.isFinite(expiry)) {
    return 0;
  }
  const startOfToday = new Date(now);
  startOfToday.setUTCHours(0, 0, 0, 0);
  return Math.max(
    0,
    Math.round((expiry - startOfToday.getTime()) / 86_400_000),
  );
};

/**
 * Derived from the calendar, because the contracts endpoint carries no
 * expiration type. Standard monthlies are the third Friday; a monthly in
 * March, June, September or December is also the quarterly.
 */
export const classifyExpirationCadence = (date: string): ExpirationCadence => {
  const parsed = parseIsoDate(date);
  if (Number.isNaN(parsed.getTime())) {
    return "weekly";
  }
  const isFriday = parsed.getUTCDay() === 5;
  const dayOfMonth = parsed.getUTCDate();
  const isThirdFriday = isFriday && dayOfMonth >= 15 && dayOfMonth <= 21;
  if (!isThirdFriday) {
    return "weekly";
  }
  const month = parsed.getUTCMonth();
  const isQuarterEnd =
    month === 2 || month === 5 || month === 8 || month === 11;
  return isQuarterEnd ? "quarterly" : "monthly";
};

/**
 * After the close the contracts endpoint still returns that day's expired
 * contracts first, with `status: "active"` and `tradable: true`, so neither
 * field can be trusted. The date has to be compared against the session.
 */
export const isExpirationSelectable = (
  date: string,
  { isMarketOpen, now = Date.now() }: { isMarketOpen?: boolean; now?: number },
): boolean => {
  const dte = daysToExpiration(date, now);
  if (dte > 0) {
    return true;
  }
  // Same-day expiry is only tradable while the session is open. When the clock
  // has not resolved yet, stay conservative and keep it out.
  return isMarketOpen === true;
};

/**
 * "Aug 12 (1d)" this year, "Jan 15, 2027 (145d)" beyond it. The year is only
 * worth its width once a date is ambiguous, which for expirations means a LEAP.
 */
export const formatExpirationLabel = (
  option: ExpirationOption,
  now = new Date(),
) => {
  const [year, , day] = option.date.split("-");
  const monthName = new Date(`${option.date}T00:00:00Z`).toLocaleString(
    "en-US",
    { month: "short", timeZone: "UTC" },
  );
  const yearSuffix =
    Number(year) === now.getFullYear() ? "" : `, ${Number(year)}`;
  return `${monthName} ${Number(day)}${yearSuffix} (${option.daysToExpiration}d)`;
};

export const buildExpirationOptions = (
  dates: readonly string[],
  { isMarketOpen, now = Date.now() }: { isMarketOpen?: boolean; now?: number },
): ExpirationOption[] =>
  dates
    .filter((date) => isExpirationSelectable(date, { isMarketOpen, now }))
    .map((date) => ({
      date,
      daysToExpiration: daysToExpiration(date, now),
      cadence: classifyExpirationCadence(date),
    }));

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const readQuote = (
  contract: OptionContractWithSnapshot,
): { quote: ChainQuote; hasMarketData: boolean } => {
  const snapshot = contract.snapshot;
  const latestQuote = snapshot?.latestQuote;
  const latestTrade = snapshot?.latestTrade;
  const greeks = snapshot?.greeks;

  // A zero bid means nobody is bidding, which is not the same as a 0.00 price.
  const bid = latestQuote?.bp ? latestQuote.bp : undefined;
  const ask = latestQuote?.ap ?? undefined;
  const last = latestTrade?.p ?? undefined;
  const previousClose = snapshot?.prevDailyBar?.c;

  // Only from a two-sided quote: a midpoint off one side is a made-up price.
  const mark =
    bid !== undefined && ask !== undefined ? (bid + ask) / 2 : undefined;

  // Against the previous close, and from the last trade rather than today's bar
  // close, so Last and Net change always agree. `dailyBar.c` can disagree wildly
  // with the last trade on a thin contract.
  const netChange =
    last !== undefined && previousClose !== undefined && previousClose > 0
      ? last - previousClose
      : undefined;

  const quote: ChainQuote = {
    bid,
    ask,
    quoteAt: toEpochMs(latestQuote?.t),
    last,
    lastAt: toEpochMs(latestTrade?.t),
    mark,
    netChange,
    changePercent:
      netChange !== undefined && previousClose
        ? (netChange / previousClose) * 100
        : undefined,
    volume: snapshot?.dailyBar?.v ?? undefined,
    openInterest: toNumber(contract.open_interest),
    openInterestDate: contract.open_interest_date ?? undefined,
    impliedVolatility: snapshot?.impliedVolatility ?? undefined,
    greeks: greeks
      ? {
          delta: greeks.delta,
          gamma: greeks.gamma,
          theta: greeks.theta,
          vega: greeks.vega,
          rho: greeks.rho,
        }
      : undefined,
  };

  return { quote, hasMarketData: !!snapshot };
};

export interface BuildChainRowsArgs {
  contracts: readonly OptionContractWithSnapshot[];
  spot?: number;
}

/**
 * Pairs contracts into strike rows.
 *
 * A missing side is kept as an empty half-row rather than dropping the strike,
 * because on thinner names one contract in a chain can lack a snapshot.
 */
export const buildChainRows = ({
  contracts,
  spot,
}: BuildChainRowsArgs): ChainRow[] => {
  const byStrike = new Map<number, ChainRow>();

  for (const contract of contracts) {
    const strike = toNumber(contract.strike_price);
    if (strike === undefined) {
      continue;
    }

    const { quote, hasMarketData } = readQuote(contract);

    const isInTheMoney =
      spot === undefined
        ? false
        : contract.type === "call"
          ? strike < spot
          : strike > spot;

    const leg: ChainLeg = {
      symbol: contract.symbol,
      contract,
      quote,
      isInTheMoney,
      hasMarketData,
    };

    const row = byStrike.get(strike) ?? { strike, isAtTheMoney: false };
    if (contract.type === "call") {
      row.call = leg;
    } else {
      row.put = leg;
    }
    byStrike.set(strike, row);
  }

  const rows = [...byStrike.values()].sort((a, b) => a.strike - b.strike);
  return markAtTheMoney(rows, spot);
};

const quotesAreEqual = (a: ChainQuote, b: ChainQuote): boolean =>
  a.bid === b.bid &&
  a.ask === b.ask &&
  a.last === b.last &&
  a.netChange === b.netChange &&
  a.changePercent === b.changePercent &&
  a.volume === b.volume &&
  a.openInterest === b.openInterest &&
  a.impliedVolatility === b.impliedVolatility &&
  a.mark === b.mark &&
  a.openInterestDate === b.openInterestDate &&
  a.quoteAt === b.quoteAt &&
  a.lastAt === b.lastAt &&
  a.greeks?.delta === b.greeks?.delta &&
  a.greeks?.gamma === b.greeks?.gamma &&
  a.greeks?.theta === b.greeks?.theta &&
  a.greeks?.vega === b.greeks?.vega &&
  a.greeks?.rho === b.greeks?.rho;

const legsAreEqual = (
  a: ChainLeg | undefined,
  b: ChainLeg | undefined,
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.symbol === b.symbol &&
    a.isInTheMoney === b.isInTheMoney &&
    a.hasMarketData === b.hasMarketData &&
    quotesAreEqual(a.quote, b.quote)
  );
};

/**
 * Carries forward the previous object for any strike whose displayed values are
 * unchanged.
 *
 * Every poll rebuilds the rows from a freshly parsed payload, so identities
 * change even where nothing did and no row can be skipped on the way down. The
 * comparison covers exactly what a row renders — the strike, its marker, and
 * each side's quote — which is what makes a stable identity mean "nothing to
 * redraw" rather than merely "same strike".
 */
export const reuseUnchangedRows = (
  previous: readonly ChainRow[],
  next: readonly ChainRow[],
): ChainRow[] => {
  if (previous.length === 0) {
    return [...next];
  }
  const byStrike = new Map(previous.map((row) => [row.strike, row]));
  return next.map((row) => {
    const before = byStrike.get(row.strike);
    if (
      before &&
      before.isAtTheMoney === row.isAtTheMoney &&
      legsAreEqual(before.call, row.call) &&
      legsAreEqual(before.put, row.put)
    ) {
      return before;
    }
    // Chained readings would pin the whole session in memory, so the row that
    // was on screen is kept without whatever it in turn replaced.
    return before
      ? { ...row, previous: { ...before, previous: undefined } }
      : row;
  });
};

/**
 * Marks the strike nearest spot, or both neighbours when spot falls between two.
 * Spot almost never lands exactly on a strike, so marking the pair that brackets
 * it shows where the money sits without implying a precision the data lacks.
 */
export const markAtTheMoney = (
  rows: readonly ChainRow[],
  spot: number | undefined,
): ChainRow[] => {
  if (spot === undefined || rows.length === 0) {
    return rows.map((row) => ({ ...row, isAtTheMoney: false }));
  }

  const exact = rows.findIndex((row) => row.strike === spot);
  if (exact !== -1) {
    return rows.map((row, index) => ({
      ...row,
      isAtTheMoney: index === exact,
    }));
  }

  const above = rows.findIndex((row) => row.strike > spot);
  if (above === -1) {
    return rows.map((row, index) => ({
      ...row,
      isAtTheMoney: index === rows.length - 1,
    }));
  }
  if (above === 0) {
    return rows.map((row, index) => ({ ...row, isAtTheMoney: index === 0 }));
  }

  return rows.map((row, index) => ({
    ...row,
    isAtTheMoney: index === above || index === above - 1,
  }));
};

/** Index of the row closest to spot, used as the window centre. */
export const findAtTheMoneyIndex = (
  rows: readonly ChainRow[],
  spot: number | undefined,
): number => {
  if (rows.length === 0) {
    return 0;
  }
  const marked = rows.findIndex((row) => row.isAtTheMoney);
  if (marked !== -1) {
    return marked;
  }
  if (spot === undefined) {
    return Math.floor(rows.length / 2);
  }
  let best = 0;
  for (let index = 1; index < rows.length; index += 1) {
    if (
      Math.abs(rows[index].strike - spot) < Math.abs(rows[best].strike - spot)
    ) {
      best = index;
    }
  }
  return best;
};

/**
 * Trims the chain to `count` strikes either side of the money. `all` keeps every
 * strike, which is why the grid windows its rows.
 */
/** Narrows the offered expirations to the chosen date range. */
export const filterExpirations = (
  options: readonly ExpirationOption[],
  filter: ExpirationFilter,
  range: ExpirationRange,
): ExpirationOption[] => {
  if (filter === "all") {
    return [...options];
  }
  if (filter === "range") {
    const { from, to } = range;
    return options.filter(
      (option) => (!from || option.date >= from) && (!to || option.date <= to),
    );
  }
  if (filter === "weekly") {
    return options.filter((option) => option.cadence === "weekly");
  }
  return options.filter((option) => option.cadence !== "weekly");
};

export const windowStrikes = (
  rows: readonly ChainRow[],
  spot: number | undefined,
  count: number,
): ChainRow[] => {
  if (!Number.isFinite(count) || count <= 0 || rows.length === 0) {
    return [...rows];
  }
  const centre = findAtTheMoneyIndex(rows, spot);
  const start = Math.max(0, centre - count);
  const end = Math.min(rows.length, centre + count + 1);
  return rows.slice(start, end);
};

// ---------------------------------------------------------------------------
// Row windowing (virtualisation)
// ---------------------------------------------------------------------------

export interface RowWindow {
  startIndex: number;
  endIndex: number;
  paddingTop: number;
  paddingBottom: number;
}

/**
 * Fixed-height row virtualisation. `ALL` on a mega-cap is ~350 strikes with up
 * to four quote buttons each, so only the visible slice is mounted.
 */
export const computeRowWindow = ({
  rowCount,
  rowHeight,
  scrollTop,
  viewportHeight,
  overscan = 6,
}: {
  rowCount: number;
  rowHeight: number;
  scrollTop: number;
  viewportHeight: number;
  overscan?: number;
}): RowWindow => {
  if (rowCount === 0 || rowHeight <= 0 || viewportHeight <= 0) {
    return {
      startIndex: 0,
      endIndex: rowCount,
      paddingTop: 0,
      paddingBottom: 0,
    };
  }

  const first = Math.floor(scrollTop / rowHeight);
  const visible = Math.ceil(viewportHeight / rowHeight);
  const startIndex = Math.max(0, first - overscan);
  const endIndex = Math.min(rowCount, first + visible + overscan);

  return {
    startIndex,
    endIndex,
    paddingTop: startIndex * rowHeight,
    paddingBottom: Math.max(0, (rowCount - endIndex) * rowHeight),
  };
};
